import { AttachFile, Save } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useConfirm } from 'material-ui-confirm'
import { MuiFileInput } from 'mui-file-input'
import React, { FunctionComponent, useEffect, useState } from 'react'

import { notError, notSuccess } from '../../../../utils/notification'
import { swalException } from '../../../../utils/swal'
import { apiArticuloImportar } from '../../api/apiArticuloImportar'

interface OwnProps {
  id: string
  keepMounted: boolean
  open: boolean
  onClose: (value?: boolean) => void
}

type Props = OwnProps

const ArticuloImportarDialog: FunctionComponent<Props> = (props) => {
  const { onClose, open, ...other } = props
  const [file, setFile] = useState<File | null>(null)
  const [formLoading, setFormLoading] = useState(false)

  const dialog = useConfirm()
  /**
   * Cambio de estado
   * @param value
   */
  const onFileChange = (value: File | null) => {
    if (value) {
      if (value.size > 100000 * 5) {
        notError('Archivo no superior a 5mb')
      } else {
        setFile(value)
      }
    }
  }

  /**
   * Envia el archivo al servidor
   */
  const onSubmit = async () => {
    if (file) {
      setFormLoading(true)
      const resp = await apiArticuloImportar(1, file).catch((e) => {
        swalException(e)
        setFormLoading(false)
      })
      if (resp) {
        if (resp.success) {
          notSuccess('Importación exitosa')
          onClose(true)
        } else {
          onClose(false)
          await dialog({
            dialogProps: {
              maxWidth: 'md',
            },
            title: 'Error de importación',
            confirmationText: 'Cerrar',
            content: (
              <>
                <Typography variant={'body2'} gutterBottom>
                  Se encontraron errores al importar el archivo
                </Typography>
                <Typography variant={'body2'} gutterBottom>
                  <strong>Errores:</strong>
                </Typography>
                <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 450 }}>
                  <Table stickyHeader size="small" aria-label="a dense table">
                    <TableHead>
                      <TableRow>
                        <TableCell width={100}>Fila</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resp.detalle.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.fila}</TableCell>
                          <TableCell>{item.errores.join(', ')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ),
            hideCancelButton: true,
          })
            .then(() => {})
            .catch(() => {})
        }
      }

      setFormLoading(false)
    } else {
      notError('Seleccione una archivo')
    }
  }

  useEffect(() => {
    if (open) {
      setFile(null)
      setFormLoading(false)
    }
  }, [open])

  return (
    <Dialog maxWidth="xs" open={open} {...other}>
      <DialogTitle>Importar articulos</DialogTitle>
      <DialogContent dividers>
        <Alert color={'warning'}>
          <Typography>
            Puede descargar la plantilla de importación de articulos desde el siguiente
            enlace.
            <Link
              href="https://cloud.isipaid.net/inv/inv_template_articulos_V3.xlsx"
              variant="body2"
              sx={{ ml: 1 }}
            >
              Descargar Plantilla
            </Link>
          </Typography>
        </Alert>

        <MuiFileInput
          size="small"
          sx={{ mt: 3 }}
          value={file}
          inputProps={{ accept: '.csv' }}
          InputProps={{
            startAdornment: <AttachFile />,
          }}
          placeholder="Seleccionar archivo csv"
          onChange={onFileChange}
        />
        <small>Archivo csv separados por ;</small>
      </DialogContent>
      <DialogActions>
        <Button
          color={'error'}
          disabled={formLoading}
          onClick={() => {
            onClose()
          }}
        >
          Cerrar
        </Button>
        <LoadingButton
          size={'small'}
          color={'primary'}
          loading={formLoading}
          variant={'contained'}
          sx={{ mr: 2 }}
          startIcon={<Save />}
          onClick={onSubmit}
          disabled={!file}
        >
          Iniciar Importación
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default ArticuloImportarDialog
